import { createSlice, type PayloadAction, type Reducer } from "@reduxjs/toolkit"

import type { RootState } from "./root.reducer"

/**
 * Single source of truth for the device's network reachability. Fed by a
 * NetInfo subscription mounted once in the dashboard layout (see
 * ``useNetworkStatusSync``).
 *
 * Two flags, two consumers:
 *   * ``isOnline`` — passive NetInfo state. Drives the always-on cloud
 *     icon that sits next to the heatmap legend, so the user has a
 *     low-friction signal that the app knows it's offline.
 *   * ``userPerceivedOffline`` — flipped on when a user-initiated request
 *     fails offline (set by ``useErrorHandler``). Drives the loud
 *     "OFFLINE" pill in the top right — the one that needs to grab
 *     attention because the user just tried to do something. Cleared when
 *     ``isOnline`` flips back to true.
 *
 * ``isOnline`` starts ``true`` (optimistic) so the first second of app
 * boot — before NetInfo has reported — doesn't briefly render the
 * offline indicator on a healthy connection.
 */
interface NetworkState {
  isOnline: boolean
  userPerceivedOffline: boolean
}

const initialState: NetworkState = {
  isOnline: true,
  userPerceivedOffline: false,
}

const slice = createSlice({
  initialState,
  name: "network",
  reducers: {
    setIsOnline(state, action: PayloadAction<boolean>) {
      state.isOnline = action.payload
      if (action.payload) {
        state.userPerceivedOffline = false
      }
    },
    markUserPerceivedOffline(state) {
      state.userPerceivedOffline = true
    },
  },
})

export const { setIsOnline, markUserPerceivedOffline } = slice.actions

const network: Reducer<NetworkState> = slice.reducer
export default network

export function getIsOnlineFromState(state: RootState): boolean {
  return state.network.isOnline
}

export function getUserPerceivedOfflineFromState(state: RootState): boolean {
  return state.network.userPerceivedOffline
}
